import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, Loader2, Sparkles, TrendingUp, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  "Handmade & Artisan", "Corporate Gifting", "Home & Living", "Fashion & Accessories",
  "Beauty & Wellness", "Food & Beverage", "Sustainable Products", "Office & Workplace",
  "Manufacturing", "D2C Brands", "Consultants", "Coaches & Trainers", "HR Professionals",
  "Marketing & Branding", "Finance & Accounting", "Legal Services", "Technology & IT",
  "Creative Services", "Event Professionals", "Wellness Experts",
];

const initialForm = {
  full_name: "", email: "", phone: "", business_name: "",
  category: "", city: "", website: "", description: "",
};

function bandColor(band) {
  const b = (band || "").toLowerCase();
  if (b === "enterprise-ready") return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (b === "ready") return "text-brand-wine bg-brand-wine/10 border-brand-wine/20";
  if (b === "progressing") return "text-amber-800 bg-amber-50 border-amber-200";
  return "text-brand-muted bg-brand-paper2 border-brand-line";
}

export default function JoinDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [aiStatus, setAiStatus] = useState("idle"); // idle | pending | ready | failed
  const [aiResult, setAiResult] = useState(null);
  const pollRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (form.full_name.trim().length < 2) return "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email address.";
    if (form.business_name.trim().length < 1) return "Please enter your business name.";
    if (!form.category) return "Please select a category.";
    if (form.description.trim().length < 10) return "Please describe your business (min 10 characters).";
    return null;
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => () => stopPolling(), []);

  const startPolling = (id) => {
    stopPolling();
    let elapsed = 0;
    pollRef.current = setInterval(async () => {
      elapsed += 3;
      try {
        const res = await axios.get(`${API}/business-applications/${id}/ai`);
        if (res.data.ai_status === "ready") {
          setAiStatus("ready");
          setAiResult(res.data.ai);
          stopPolling();
        } else if (res.data.ai_status === "failed") {
          setAiStatus("failed");
          stopPolling();
        }
      } catch (e) { /* ignore */ }
      if (elapsed >= 60) { stopPolling(); setAiStatus((s) => (s === "pending" ? "failed" : s)); }
    }, 3000);
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/business-applications`, form);
      setApplicationId(res.data.id);
      setAiStatus("pending");
      startPolling(res.data.id);
      toast.success("Application submitted — generating your AI profile…");
    } catch (e2) {
      const msg = e2?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = (v) => {
    if (!v) {
      stopPolling();
      setForm(initialForm);
      setApplicationId(null);
      setAiStatus("idle");
      setAiResult(null);
    }
    onOpenChange(v);
  };

  const showSuccess = applicationId !== null;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-3xl bg-brand-cream border-brand-line p-0 overflow-hidden max-h-[90vh]"
        data-testid="join-dialog"
      >
        {showSuccess ? (
          <div className="p-6 md:p-10 overflow-y-auto max-h-[90vh]" data-testid="join-success">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-brand-wine/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-brand-wine" size={28} />
              </div>
              <h3 className="font-serif text-3xl md:text-4xl font-medium text-brand-ink">Application received</h3>
              <p className="mt-2 text-brand-muted max-w-lg mx-auto leading-relaxed">
                Our curation team will review shortly. Meanwhile, here&apos;s your instant AI procurement-readiness analysis:
              </p>
            </div>

            <div className="mt-8">
              {aiStatus === "pending" && (
                <div className="p-8 bg-white border border-brand-line rounded-2xl text-center" data-testid="ai-loading">
                  <Loader2 size={26} className="animate-spin text-brand-wine mx-auto" />
                  <p className="mt-4 font-serif text-xl text-brand-ink">Generating your standardized profile…</p>
                  <p className="mt-2 text-sm text-brand-muted">Claude Sonnet 4.5 is analyzing your business (10–20s).</p>
                </div>
              )}

              {aiStatus === "failed" && (
                <div className="p-6 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 flex items-start gap-3" data-testid="ai-failed">
                  <AlertCircle size={20} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">AI profile is still cooking</p>
                    <p className="text-sm mt-1">Our team will still review your application manually. Thank you for your patience.</p>
                  </div>
                </div>
              )}

              {aiStatus === "ready" && aiResult && (
                <div className="space-y-6" data-testid="ai-result">
                  <div className="bg-white rounded-2xl border border-brand-line p-6 md:p-7">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine flex items-center gap-1.5">
                          <Sparkles size={13} /> AI-Standardized Profile
                        </p>
                        {aiResult.tagline && (
                          <p className="mt-3 font-serif italic text-2xl text-brand-ink leading-snug">
                            &ldquo;{aiResult.tagline}&rdquo;
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <ScoreCircle score={aiResult.readiness_score} />
                        <div>
                          <p className="text-[11px] uppercase tracking-widest text-brand-muted">Readiness</p>
                          <span className={`inline-block mt-0.5 text-xs font-semibold px-2.5 py-0.5 rounded-full border ${bandColor(aiResult.readiness_band)}`}>
                            {aiResult.readiness_band}
                          </span>
                        </div>
                      </div>
                    </div>
                    {aiResult.elevator_pitch && (
                      <p className="mt-4 text-[15px] text-brand-ink leading-relaxed">
                        {aiResult.elevator_pitch}
                      </p>
                    )}
                  </div>

                  {aiResult.capabilities?.length > 0 && (
                    <div>
                      <h4 className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine mb-2">
                        Capabilities
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {aiResult.capabilities.map((c) => (
                          <span key={c} className="text-sm bg-white border border-brand-line rounded-full px-3 py-1.5">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiResult.recommendations?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-brand-line p-6">
                      <h4 className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine mb-3 flex items-center gap-1.5">
                        <TrendingUp size={12} /> Personalized recommendations to become enterprise-ready
                      </h4>
                      <ol className="space-y-2 text-[14px] text-brand-ink list-decimal pl-5">
                        {aiResult.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => close(false)}
                className="bg-brand-wine text-white rounded-lg px-7 py-3 font-medium hover:bg-brand-wineDark transition-colors"
                data-testid="join-success-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="p-8 md:p-10 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine">
                For Businesses
              </span>
              <DialogTitle className="mt-2 font-serif text-3xl md:text-4xl font-medium text-brand-ink leading-tight">
                Join the <span className="italic text-brand-wine">Business Exchange</span>
              </DialogTitle>
              <DialogDescription className="text-brand-muted text-[15px] leading-relaxed">
                Share a few details. You&apos;ll instantly receive an AI-powered procurement-readiness score and standardized business profile.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="join-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full Name *"><input type="text" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={inputCls} placeholder="Jane Doe" data-testid="join-input-name" /></Field>
                <Field label="Email *"><input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="jane@business.com" data-testid="join-input-email" /></Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Business Name *"><input type="text" value={form.business_name} onChange={(e) => update("business_name", e.target.value)} className={inputCls} placeholder="Aura Textiles" data-testid="join-input-business-name" /></Field>
                <Field label="Category *">
                  <Select value={form.category} onValueChange={(v) => update("category", v)}>
                    <SelectTrigger className="bg-white border-brand-line rounded-xl h-[50px] px-4 focus:ring-brand-wine/30 focus:border-brand-wine" data-testid="join-input-category">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-brand-line max-h-72">
                      {categories.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`join-category-option-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="City"><input type="text" value={form.city} onChange={(e) => update("city", e.target.value)} className={inputCls} placeholder="Mumbai" data-testid="join-input-city" /></Field>
                <Field label="Phone"><input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="+91 98765 43210" data-testid="join-input-phone" /></Field>
              </div>

              <Field label="Website"><input type="url" value={form.website} onChange={(e) => update("website", e.target.value)} className={inputCls} placeholder="https://yourbrand.com" data-testid="join-input-website" /></Field>

              <Field label="Tell us about your business *">
                <textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={4} className={`${inputCls} !h-auto resize-none`} placeholder="What do you offer, who do you serve, what makes you unique, certifications, notable clients, production capacity, etc." data-testid="join-input-description" />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-4 bg-brand-wine text-white rounded-lg py-4 font-semibold hover:bg-brand-wineDark transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="join-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Submit &amp; get instant AI readiness score
                  </>
                )}
              </button>
              <p className="text-center text-xs text-brand-muted mt-2">
                By submitting, you agree to Yuukke&apos;s Privacy Policy &amp; Terms.
              </p>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "w-full bg-white border border-brand-line rounded-xl px-4 py-3 h-[50px] focus:outline-none focus:ring-2 focus:ring-brand-wine/20 focus:border-brand-wine transition-all text-brand-ink placeholder:text-brand-muted/50";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-medium text-brand-ink mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function ScoreCircle({ score }) {
  const s = Number(score) || 0;
  const color = s >= 85 ? "bg-emerald-600" : s >= 70 ? "bg-brand-wine" : s >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className={`w-14 h-14 rounded-full ${color} text-white flex items-center justify-center font-serif text-xl font-medium shadow-[0_10px_20px_-10px_rgba(139,30,63,0.5)]`}>
      {s}
    </div>
  );
}
